import axios from "axios";
import { trackEvent, trackException, trackMetric } from "../telemetry";
import { handleLogout } from "../utils/authUtils";

/**
 * Creates a configured axios instance with refresh token handling and telemetry
 * @param {Object} config - Configuration object
 * @param {string} config.baseURL - Base URL for the axios instance
 * @param {string} config.tokenKey - localStorage key for the access token (e.g., 'authToken', 'adminToken', 'employeeToken')
 * @param {string} config.userType - User type for telemetry ('customer', 'admin', 'employee')
 * @param {string} config.endpointPrefix - Prefix for endpoint in telemetry (e.g., '', '/admin', '/emp')
 * @param {string} config.role - Role from ROLES enum (e.g., ROLES.CUSTOMER, ROLES.ADMIN, ROLES.EMPLOYEE)
 * @returns {Object} Configured axios instance
 */
export const createAxiosInstance = ({
  baseURL,
  tokenKey,
  userType,
  endpointPrefix = "",
  role,
}) => {
  // Construct context name from role (capitalize first letter, e.g., 'customer' -> 'Customer_Token_Refresh_Failed')
  const capitalizedRole = role.charAt(0).toUpperCase() + role.slice(1);
  const contextName = `${capitalizedRole}_Token_Refresh_Failed`;
  // Track if refresh is in progress to prevent multiple simultaneous refresh calls
  let isRefreshing = false;
  let failedQueue = [];

  // Process queued requests after token refresh
  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    failedQueue = [];
  };

  // Function to refresh the token
  const refreshToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    if (!refreshToken) {
      handleLogout();
      throw new Error("No refresh token available");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/refresh`,
        {}, // Empty body
        {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        }
      );
      const { token } = response.data.data;
      localStorage.setItem(tokenKey, token);
      return token;
    } catch (error) {
      // If refresh fails, logout the user
      handleLogout();
      throw new Error("Failed to refresh token");
    }
  };

  // Create axios instance
  const axiosInstance = axios.create({
    baseURL,
    timeout: 1000000,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor to include the token and track timing
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add request start time for performance tracking
      config.metadata = { startTime: Date.now() };

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token expiration and track API performance
  axiosInstance.interceptors.response.use(
    (response) => {
      // Calculate request duration
      const duration = response.config.metadata
        ? Date.now() - response.config.metadata.startTime
        : 0;

      // Extract endpoint from URL (remove base URL and query params)
      const endpoint = response.config.url?.split("?")[0] || "unknown";
      const method = response.config.method?.toUpperCase() || "GET";

      // Track slow API calls (> 3 seconds)
      if (duration > 3000) {
        trackMetric("Slow_API_Call_Ms", duration, {
          endpoint: endpointPrefix + endpoint,
          method,
          statusCode: response.status,
          userType,
        });
      }

      return response;
    },
    async (error) => {
      const { config, response } = error;
      const originalRequest = config;

      // Calculate request duration for failed requests
      const duration = config?.metadata
        ? Date.now() - config.metadata.startTime
        : 0;
      const endpoint = config?.url?.split("?")[0] || "unknown";
      const method = config?.method?.toUpperCase() || "GET";

      // Handle authentication errors
      if (response?.status) {
        const status = response.status;
        const errorMessage = response.data?.message || "";

        // Handle 401 (expired token) - attempt refresh
        if (status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            // If refresh is already in progress, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                // Mark as retried to prevent infinite refresh loops if retry fails
                originalRequest._retry = true;
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axiosInstance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const newToken = await refreshToken();
            isRefreshing = false;
            processQueue(null, newToken);

            window.dispatchEvent(
              new CustomEvent("auth:tokenRefreshed", {
                detail: { tokenKey, role },
              })
            );

            // Retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            processQueue(refreshError, null);
            // Handle token refresh failure - logout already handled in refreshToken function
            trackException(refreshError, { context: contextName });
            return Promise.reject(refreshError);
          }
        }

        // Handle 403 (invalid/forbidden token) - logout immediately
        if (status === 403) {
          handleLogout();
          return Promise.reject(error);
        }

        // Track API errors (except 401/403 which are auth-related)
        if (status !== 401 && status !== 403) {
          trackEvent("API_Error", {
            endpoint: endpointPrefix + endpoint,
            method,
            statusCode: status,
            durationMs: duration,
            errorMessage: errorMessage,
            userType,
            timestamp: new Date().toISOString(),
          });
        }
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
